import React, { useState, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useOkapiKy } from '@folio/stripes/core';
import { useQueryClient } from '@tanstack/react-query';
import { buildUrl } from '@folio/stripes/smart-components';
import ScheduledReportsList from '../components/scheduled/ScheduledReportsList';
import useScheduledReportsListAPI from '../hooks/useScheduledReportsListAPI';

const PER_PAGE = 30;

function ScheduledReportsRoute() {
  const location     = useLocation();
  const history      = useHistory();
  const okapiKy      = useOkapiKy();
  const queryClient  = useQueryClient();

  const searchParams = new URLSearchParams(location.search);
  const [offset, setOffset] = useState(0);

  const { scheduledReports, totalRecords, isLoading, isFetching, refetch } =
    useScheduledReportsListAPI({ searchParams, offset });

  const querySetter = useCallback(({ nsValues, state }) => {
    if (state?.sortChanged) setOffset(0);

    const url = buildUrl(location, nsValues);
    if (`${location.pathname}${location.search}` !== url) {
      history.push(url);
    }
  }, [location, history]);

  const queryGetter = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const result = {};
    params.forEach((value, key) => { result[key] = value; });
    return result;
  }, [location.search]);

  const onNeedMoreData = useCallback((askAmount, index) => {
    if (index >= 0) {
      setOffset(index);
    } else {
      setOffset(prev => prev + PER_PAGE);
    }
  }, []);

  // Source-like object for ScheduledReportsList compatibility
  const source = {
    totalCount:  () => totalRecords,
    loaded:      () => !isLoading,
    pending:     () => isFetching,
    failure:     () => false,
    fetchMore:   () => setOffset(prev => prev + PER_PAGE),
    fetchOffset: (index) => setOffset(index),
    update:      () => {},
  };

  // Resources-like object
  const resources = {
    scheduledReports: {
      records:    scheduledReports,
      other:      { totalRecords },
      hasLoaded:  !isLoading,
    },
  };

  // Mutator-like object
  const mutator = {
    resultOffset:    { replace: (val) => setOffset(val) },
    query:           { update: () => {}, replace: () => {} },
    activeRecord:    { update: () => {} },
    scheduledReports: {
      DELETE: ({ id }) =>
        okapiKy.delete(`scheduled-reports/${id}`).then(() => {
          queryClient.invalidateQueries(['scheduled-reports-list']);
          refetch();
        }),
    },
  };

  return (
    <ScheduledReportsList
      source={source}
      initialSearch="?sort=name"
      onNeedMoreData={onNeedMoreData}
      queryGetter={queryGetter}
      querySetter={querySetter}
      resources={resources}
      mutator={mutator}
      history={history}
      location={location}
      onRefresh={refetch}
    />
  );
}

export default ScheduledReportsRoute;
